import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { usePermissions } from "../../PermissionsProvider";
import { ConfigService } from "../../services/ConfigService";
import { ModelConfig } from "../../model_gen/config";
import { Agent } from "../../model_gen/agent_base";

interface AgentSettings {
  // AgentRequest fields
  max_iterations?: number | null;
  execution_mode?: string | null;
  high_performance?: boolean | null;
  disable_chaining?: boolean | null;

  // ChatCompletionParams fields
  model?: string | null;
}

interface ModelInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  companyId?: number;
  showEmptyOption?: boolean;
  intentGroupId?: number;
  modelType?: "all" | "server" | "client";
  agentSettings?: AgentSettings | null;
  onAgentParamsChange?: (params: {
    agent_id: number | null;
    settings: AgentSettings | null;
  }) => void;
}

const ModelInput: React.FC<ModelInputProps> = ({
  value,
  onChange,
  placeholder = "Select a model",
  label = "Model",
  disabled = false,
  style = {},
  companyId = -1,
  showEmptyOption = true,
  intentGroupId,
  modelType = "all",
  agentSettings: externalAgentSettings,
  onAgentParamsChange,
}) => {
  const { apiKey } = usePermissions();
  const [models, setModels] = useState<string[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  // Use external agentSettings as source of truth, with fallback to empty object
  const agentSettings = useMemo(() => {
    return externalAgentSettings || {};
  }, [externalAgentSettings]);

  useEffect(() => {
    setIsLoading(true);
    const modelsSubscription = ConfigService.getModels(
      intentGroupId ?? companyId,
      null,
      apiKey
    ).subscribe(
      (modelData: ModelConfig) => {
        let modelsToDisplay: string[] = [];

        if (modelType === "server" && modelData.server_models) {
          modelsToDisplay = modelData.server_models;
        } else if (modelType === "client" && modelData.client_models) {
          modelsToDisplay = modelData.client_models;
        } else {
          modelsToDisplay = modelData.all_models || [];
        }

        setModels(modelsToDisplay);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setIsLoading(false);
      }
    );

    return () => {
      modelsSubscription.unsubscribe();
    };
  }, [apiKey, companyId, intentGroupId, modelType]);

  // Fetch agents only once per apiKey
  const fetchedAgentsApiKeyRef = useRef<any>(null);
  useEffect(() => {
    if (!apiKey) return;
    if (fetchedAgentsApiKeyRef.current === apiKey && agents.length > 0) return;
    fetchedAgentsApiKeyRef.current = apiKey;

    const agentsSubscription = ConfigService.getAgents(null, apiKey).subscribe(
      (response: any) => {
        const fetchedAgents = Array.isArray(response)
          ? response
          : response.agents || [];
        setAgents(fetchedAgents);
      },
      (error: any) => {
        console.error("Error fetching agents:", error);
      }
    );

    return () => {
      agentsSubscription.unsubscribe();
    };
  }, [apiKey]);

  // Helper functions
  const groupModelsByProvider = (models: string[]) => {
    const grouped: { [provider: string]: string[] } = {};
    models.forEach((model) => {
      const [provider] = model.split(":");
      if (!grouped[provider]) {
        grouped[provider] = [];
      }
      if (!grouped[provider].includes(model)) {
        grouped[provider].push(model);
      }
    });
    return grouped;
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to check if selected value is an agent
  const isAgentSelected = (value?: string) => {
    return value?.startsWith("agent:");
  };

  // Helper function to get selected agent
  const getSelectedAgent = (value?: string) => {
    if (!isAgentSelected(value)) return null;
    const agentId = value?.replace("agent:", "");
    return agents.find((agent) => agent.id?.toString() === agentId) || null;
  };

  // Update selected agent when value changes
  useEffect(() => {
    const agent = getSelectedAgent(value);
    setSelectedAgent(agent);
  }, [value, agents]);

  const handleValueChange = (newValue: string) => {
    console.log("ModelInput handleValueChange called with:", newValue);
    console.log("onChange prop exists:", !!onChange);
    if (onChange) {
      console.log("Calling onChange with:", newValue);
      onChange(newValue);
    }
    if (onAgentParamsChange) {
      if (isAgentSelected(newValue)) {
        const agent = getSelectedAgent(newValue);
        const defaults: AgentSettings = {
          max_iterations: agent?.max_iterations || null,
          execution_mode: "route",
          high_performance: true,
          disable_chaining: true,
          model: "openai:gpt-4o-mini",
        };
        onAgentParamsChange({
          agent_id: agent?.id ? Number(agent.id) : null,
          settings: defaults,
        });
      } else {
        onAgentParamsChange({ agent_id: null, settings: null });
      }
    }
  };

  // Group and sort models - memoized to avoid recalculations on every render
  const { groupedModels, sortedProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(models);
    const sorted = Object.keys(grouped).sort((a, b) => {
      if (a.toLowerCase() === "maitai") return -1;
      if (b.toLowerCase() === "maitai") return 1;
      return a.localeCompare(b);
    });
    return { groupedModels: grouped, sortedProviders: sorted };
  }, [models]);

  return (
    <div style={style}>
      <Select
        value={value || undefined}
        onValueChange={handleValueChange}
        disabled={disabled || isLoading}>
        <SelectTrigger className="w-full focus:ring-maitai-green focus:border-maitai-green">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-w-[400px]">
          {agents.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-maitai-green font-gc-med">
                Agent
              </SelectLabel>
              {agents.map((agent) => (
                <SelectItem
                  key={`agent:${agent.id}`}
                  value={`agent:${agent.id}`}>
                  {agent.agent_name}
                </SelectItem>
              ))}
            </SelectGroup>
          )}

          {sortedProviders.map((provider) => (
            <SelectGroup key={provider}>
              <SelectLabel
                className={
                  provider.toLowerCase() === "maitai"
                    ? "text-maitai-green font-gc-med"
                    : "font-gc-med"
                }>
                {provider.toLowerCase() === "maitai"
                  ? `${capitalizeFirstLetter(provider)} (Recommended)`
                  : capitalizeFirstLetter(provider)}
              </SelectLabel>
              {groupedModels[provider].map((model) => {
                const [, ...modelParts] = model.split(":");
                const modelName = modelParts.join(":");
                return (
                  <SelectItem key={model} value={model}>
                    {modelName}
                  </SelectItem>
                );
              })}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ModelInput;
